import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import VueRecaptcha from 'vue-recaptcha';
import { TokenizedPaymentCard, ApiValidationError, ErrorMode, FormField, ValidationError } from '@/models';
import { CustomerProfile } from '@/models/customer';
import { AxiosError } from 'axios';
import { makeFormField, enforceMaxLength, stripNonNumerics } from '@/helpers';
import { ui } from '@/constants';
import { AccountManagementEvent, ErrorEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';

@Component({
  name: 'CmgAddGiftCardModal',
  components: {
    VueRecaptcha,
  },
})
export default class CmgAddGiftCardModal extends Vue {
  giftCard = makeFormField();
  isFormValid = true;
  errorMessage = '';
  siteKey = this.$services.config.getRecaptchaKey();
  isLoading = false;
  recaptchaRendered = false;

  get blankGiftCardImgUrl(): string {
    return this.$appStore.state.cms.images.blankGiftCard.default;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.addGiftCardModal.visible;
  }

  validateGiftCardNumber(): void {
    if (this.giftCard.value.length == 0) {
      this.giftCard.error = 'Gift Card Number Required';
      this.giftCard.errorMode = ErrorMode.PlaceHolder;
    } else {
      this.giftCard.error = !/^\d{16}$/.test(this.giftCard.value) ? 'Invalid Gift Card Number' : '';
      this.giftCard.errorMode = ErrorMode.BelowField;
    }
  }

  validateForm(): void {
    this.isFormValid = true;
    this.validateGiftCardNumber();

    if (!!this.giftCard.error) {
      this.isFormValid = false;
      this.giftCard.dirty = true;
      this.$services.analytics.send(new ErrorEvent('payment', this.giftCard.error ?? '', 'add-gift-card'));
    }
  }

  enforceMaxLength(value: string, validLengths: number[], formField: FormField): string {
    return enforceMaxLength(value, validLengths, formField);
  }

  async onRecaptchaVerify(token: string): Promise<void> {
    this.isLoading = true;
    const customer = this.$appStore.state.customer.profile as CustomerProfile;
    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;
    const paymentProviders = await this.$services.transaction.getProviders(restaurant ? restaurant.restaurantNumber.toString() : undefined);
    const paymentProviderId = paymentProviders[0].paymentProviderId;
    const giftCard: TokenizedPaymentCard = {
      cardHolderName: `${customer.firstName} ${customer.lastName}`,
      paymentMethod: 'ValueLink',
      paymentProviderId,
      tokenizedAccountNumber: this.giftCard.value,
      recaptchaToken: token,
    };
    this.savePaymentCard(giftCard);
  }

  onRecaptchaExpired(response: any): void {
    (this.$refs.invisibleRecaptcha as any).reset();
  }

  onRecaptchaRender(): void {
    this.recaptchaRendered = true;
  }

  savePaymentCard(giftCard: TokenizedPaymentCard): void {
    this.$services.transaction
      .savePaymentCard(giftCard)
      .then(() => {
        this.onCloseSave();
        Vue.nextTick(() => {
          setTimeout(() => {
            const focusNearby = document.getElementsByClassName('pay-total')[0] as HTMLElement;
            const removeGiftbtn = document.getElementsByClassName('remove-gift-btn')[0] as HTMLElement;
            if (focusNearby) {
              focusNearby.focus();
            } else {
              removeGiftbtn.focus();
            }
          }, 3000);
        });
        try {
          this.$services.transaction.refreshPaymentCards();
        } catch (err) {
          // TODO-25ck23: Handle error in UI
        }
      })
      .catch((axiosError: AxiosError<ApiValidationError, any>) => {
        if (!axiosError || !axiosError.response || !axiosError.response.data) {
          return;
        }

        const validationError: ApiValidationError = axiosError.response.data;

        if (validationError.category == 'Validation') {
          const limitGiftCards = validationError.validationErrors.find(e => e.errorCode == 'TRN-0051');
          if (limitGiftCards) {
            this.errorMessage = 'You can only add a limited number of gift cards per day. Please try again in 24 hours.';
          } else {
            this.errorMessage = 'We encountered an error saving your gift card. Please check your gift card number and try again.';
          }
        }
        (this.$refs.invisibleRecaptcha as any).reset();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  onCloseSave(){
    this.initializeForm();
    this.$services.ui.setModalVisibility('addGiftCardModal', false);
  }

  onClose() {
    this.initializeForm();
    this.$services.ui.setModalVisibility('addGiftCardModal', false);
    this.$services.ui.focusBack();
  }

  initializeForm(): void {
    // Clear all of the form fields
    this.giftCard = makeFormField();

    this.isFormValid = true;
    this.errorMessage = '';
  }

  stripNonNumerics(value: string): string {
    return stripNonNumerics(value);
  }

  onSubmit(): void {
    this.$services.ui.clearLastSelectedElement();
    this.validateForm();

    if (!this.isFormValid || !this.recaptchaRendered) {
      return;
    }

    this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.AddGiftCard));

    (this.$refs.invisibleRecaptcha as any).execute();
  }
}
